import { ObservableInputsManager } from '@/shared/utils/angular-sdk';
import { UiFormFieldComponent } from '@/ui/form-field/form-field/ui-form-field';
import { UiSelectComponent } from '@/ui/select/ui-select';
import { UiToggleComponent } from '@/ui/toggle/ui-toggle';
import { observeControlValue$ } from '@/utils/angular/forms/reactive-forms/reactive-forms-rxjs-interop.utils';
import { doNotSort } from '@/utils/toolkit/array/sort.utils';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, type OnChanges, type OnInit, Output, type SimpleChanges } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, type UntypedFormGroup, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { combineLatest, type Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, withLatestFrom } from 'rxjs/operators';
import { SortOrder } from '../../../../data-viz/data-source.interface';
import { AdvizeoInputNumberDirective } from '../../../../directives/advizeo-input/advizeo-input-number.directive';
import { AdvizeoInputDirective } from '../../../../directives/advizeo-input/advizeo-input.directive';
import {
    type BusinessIntelligenceDimension,
    type BusinessIntelligenceMeasure,
    type BusinessIntelligenceSortCriterion,
    BusinessIntelligenceTemporalDimensionStep
} from '../../../../interfaces/BusinessIntelligence.interface';
import type { BusinessIntelligenceBarChartRepresentationConfig } from '../../../../interfaces/BusinessIntelligenceRepresentation.interface';
import type { BusinessIntelligenceDimensionConfiguration } from '../../../../interfaces/TailoredWidget.interface';
import { BusinessIntelligenceProvider } from '../../../../providers/business-intelligence/business-intelligence.provider';
import { BusinessIntelligenceRepresentationConfigEditorComponent } from '../business-intelligence-representation-config-editor.class';

@UntilDestroy()
@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./business-intelligence-bar-chart-representation-config-editor.scss'],
    selector: 'business-intelligence-bar-chart-representation-config-editor',
    templateUrl: 'business-intelligence-bar-chart-representation-config-editor.html',
    imports: [
        ReactiveFormsModule,
        UiFormFieldComponent,
        UiSelectComponent,
        AdvizeoInputDirective,
        AdvizeoInputNumberDirective,
        UiToggleComponent,
        AsyncPipe,
        TranslatePipe
    ]
})
export class BusinessIntelligenceBarChartRepresentationConfigEditorComponent
    extends BusinessIntelligenceRepresentationConfigEditorComponent<BusinessIntelligenceBarChartRepresentationConfig>
    implements OnInit, OnChanges
{
    private _businessIntelligenceProvider = inject(BusinessIntelligenceProvider);
    private _fb = inject(UntypedFormBuilder);
    private _translateService = inject(TranslateService);

    private _observableInputsManager = new ObservableInputsManager<BusinessIntelligenceBarChartRepresentationConfigEditorComponent>(this);

    @Input() config!: BusinessIntelligenceBarChartRepresentationConfig;
    config$ = this._observableInputsManager.observe('config');

    @Input() availableMeasures: BusinessIntelligenceMeasure[] = [];
    availableMeasures$ = this._observableInputsManager.observe('availableMeasures');

    @Input() availableDimensions: BusinessIntelligenceDimension[] = [];
    availableDimensions$ = this._observableInputsManager.observe('availableDimensions');

    @Input() disabled!: boolean;

    @Output() configChanged: EventEmitter<BusinessIntelligenceBarChartRepresentationConfig> = new EventEmitter();

    formGroup!: UntypedFormGroup;
    segmentationDimensions$!: Observable<BusinessIntelligenceDimension[]>;
    isMeasureSegmentable$!: Observable<boolean>;

    stepList = Object.values(BusinessIntelligenceTemporalDimensionStep).map(step => {
        return {
            value: step,
            label: this._translateService.instant('STEPS.' + step.toUpperCase())
        };
    });

    doNotSort = doNotSort;

    ngOnInit() {
        this.segmentationDimensions$ = this.availableDimensions$.pipe(
            filter(isNotNullish),
            map(dimensions => dimensions.filter(dimension => !this._businessIntelligenceProvider.isContinuousDimension(dimension)))
        );

        this.formGroup = this._fb.group({
            measureCode: [null, Validators.required],
            categorizationDimensionCode: [null, Validators.required],
            segmentationDimensionCode: [null],
            isSortReversed: [false],
            limit: [null],
            shouldZeroBeDisplayed: [false]
        });
        this.isMeasureSegmentable$ = combineLatest([
            this.availableMeasures$.pipe(filter(isNotNullish)),
            observeControlValue$(this.formGroup.controls.measureCode)
        ]).pipe(
            map(([availableMeasures, measureCode]) => {
                const measure = availableMeasures.find(m => m.measureCode === measureCode);
                return measure ? measure.isSegmentable : true;
            })
        );

        this.config$.pipe(untilDestroyed(this)).subscribe(config => {
            if (!config) {
                this.formGroup.setValue(
                    {
                        measureCode: null,
                        categorizationDimensionCode: null,
                        segmentationDimensionCode: null,
                        isSortReversed: false,
                        limit: null,
                        shouldZeroBeDisplayed: false
                    },
                    { emitEvent: false }
                );
            } else {
                this.formGroup.setValue(
                    {
                        measureCode: config.measure ? config.measure.measureCode : null,
                        categorizationDimensionCode: config.categorization ? config.categorization.dimensionCode : null,
                        segmentationDimensionCode: config.segmentation ? config.segmentation.dimensionCode : null,
                        isSortReversed: this.isSortReversed(config),
                        limit: isNotNullish(config.limit) ? config.limit : null,
                        shouldZeroBeDisplayed: config.shouldZeroBeDisplayed ?? false // default is for retro-compat
                    },
                    { emitEvent: false }
                );
            }
        });

        this.formGroup.valueChanges
            .pipe(
                withLatestFrom(
                    this.availableDimensions$.pipe(distinctUntilChanged()),
                    this.availableMeasures$.pipe(distinctUntilChanged())
                ),
                untilDestroyed(this)
            )
            .subscribe(([formValue, availableDimensions, availableMeasures]) => {
                const measure: BusinessIntelligenceMeasure = availableMeasures.find(m => m.measureCode === formValue.measureCode)!;

                const segmentationDimension: BusinessIntelligenceDimensionConfiguration | null =
                    formValue.segmentationDimensionCode && measure && measure.isSegmentable
                        ? {
                              dimensionCode: formValue.segmentationDimensionCode
                          }
                        : null;

                const categorizationDimension: BusinessIntelligenceDimensionConfiguration | null = formValue.categorizationDimensionCode
                    ? {
                          dimensionCode: formValue.categorizationDimensionCode
                      }
                    : null;
                const sortCriteria: BusinessIntelligenceSortCriterion[] = this.getSortCriteria(formValue, availableDimensions);
                this.configChanged.emit({
                    categorization: categorizationDimension,
                    segmentation: segmentationDimension,
                    measure: {
                        measureCode: formValue.measureCode
                    },
                    sortCriteria,
                    limit: !segmentationDimension ? formValue.limit : null,
                    shouldZeroBeDisplayed: formValue.shouldZeroBeDisplayed
                });
            });
    }

    ngOnChanges(changes: SimpleChanges) {
        this._observableInputsManager.triggerChanges(changes);
    }

    private getSortCriteria(formValue: any, availableDimensions: BusinessIntelligenceDimension[]): BusinessIntelligenceSortCriterion[] {
        if (formValue.segmentationDimensionCode) {
            return [];
        }
        if (formValue.categorizationDimensionCode) {
            const categorizationDimension = availableDimensions.find(
                dimension => dimension.dimensionCode === formValue.categorizationDimensionCode
            );
            if (this._businessIntelligenceProvider.isContinuousDimension(categorizationDimension)) {
                return [
                    {
                        memberCode: formValue.categorizationDimensionCode,
                        sortDirection: formValue.isSortReversed ? SortOrder.DESC : SortOrder.ASC
                    } as BusinessIntelligenceSortCriterion
                ];
            }
        }
        if (formValue.measureCode) {
            return [
                {
                    memberCode: formValue.measureCode,
                    sortDirection: formValue.isSortReversed ? SortOrder.ASC : SortOrder.DESC
                } as BusinessIntelligenceSortCriterion
            ];
        }
        return [];
    }

    private isSortReversed(config: BusinessIntelligenceBarChartRepresentationConfig): boolean {
        if (!config.sortCriteria || !config.sortCriteria.length) {
            return false;
        }
        const significantSortCriterion = config.sortCriteria[0];
        if (config.categorization && significantSortCriterion.memberCode === config.categorization.dimensionCode) {
            return significantSortCriterion.sortDirection === SortOrder.DESC;
        }
        if (config.measure && significantSortCriterion.memberCode === config.measure.measureCode) {
            return significantSortCriterion.sortDirection === SortOrder.ASC;
        }
        return false;
    }
}
